import type { SxProps, Theme } from '@mui/material';
import type { CSSProperties } from 'react';

// ─── Screen-reader only (visually hidden) ─────────────────────────────────────

export const srOnlySx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0
};

// ─── Dialog ───────────────────────────────────────────────────────────────────

export const dialogSx: SxProps<Theme> = { alignItems: 'flex-end' };

export const dialogPaperSx: SxProps<Theme> = {
  width: '100vw',
  maxWidth: '100vw',
  m: 0,
  borderRadius: { xs: 0, md: '16px 16px 0 0' },
  height: { xs: '100dvh', sm: '90vh' },
  maxHeight: { xs: '100dvh', sm: '90vh' },
  position: 'fixed',
  bottom: 0,
  top: { xs: 0, sm: 'auto' },
  '&:focus-visible': {
    outline: '0px'
  },
  '& :focus-visible': {
    outline: '2px solid black',
    outlineOffset: '1px'
  }
};

// ─── Modal header ─────────────────────────────────────────────────────────────

export const getDialogTitleSx = (hasSubtitle: boolean): SxProps<Theme> => ({
  fontFamily: 'AC Nord Display, sans-serif',
  fontWeight: 700,
  fontStyle: 'bold',
  fontSize: { xs: '20px', sm: '24px', md: '28px' },
  lineHeight: '36px',
  letterSpacing: '0px',
  textAlign: 'center',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  border: 0,
  mt: '40px',
  pb: hasSubtitle ? 1 : 1.5,
  mb: { xs: 1, sm: 2, md: 3 }
});

export const subtitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: { xs: '14px', sm: '18px' },
  lineHeight: '24px',
  letterSpacing: '0px',
  textAlign: 'center',
  color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
  background: 'transparent',
  mt: 1
};

export const closeButtonSx: SxProps<Theme> = {
  position: 'absolute',
  right: '20px',
  top: '20px',
  width: 40,
  height: 40,
  padding: '8px',
  borderRadius: '24px',
  bgcolor: '#FFFFFF',
  boxShadow: '0px 0px 15px 0px #00000026',
  gap: '6px',
  border: 'none'
};

// ─── TextField ────────────────────────────────────────────────────────────────

export const textFieldLabelSx = {
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  '&.Mui-focused': {
    color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
    fontWeight: 500,
    fontSize: '16px'
  },
  '&.MuiInputLabel-shrink': {
    color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
    fontWeight: 500,
    fontSize: '16px'
  }
};

export const textFieldInputSx = {
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

export const textFieldUnderlineSx = {
  '& .MuiInput-underline:before': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  },
  '& .MuiInput-underline.Mui-focused:after': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  }
};

export const textFieldErrorUnderlineSx = {
  '& .MuiInput-underline:before': {
    borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)'
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)'
  }
};

export const fieldErrorSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontSize: '11px',
  fontWeight: 400,
  color: 'var(--Sem-text-colour-text-error, #851109)',
  mt: '4px'
};

export const helperTextSx = {
  '& .MuiFormHelperText-root': {
    fontFamily: 'AC Nord Text',
    fontSize: '11px',
    fontWeight: 400,
    color: '#888',
    mt: '4px',
    ml: 0
  }
};

// ─── Step 1 – country selection ───────────────────────────────────────────────

export const step1ContentSx: SxProps<Theme> = {
  display: 'flex',
  overflow: 'auto',
  justifyContent: 'center',
  width: '100%',
  flex: 1,
  minHeight: 0,
  p: 0
};

export const leftPanelSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: { xs: 'auto', md: 0 }
};

export const searchFieldSx: SxProps<Theme> = {
  pl: 0,
  '& .MuiOutlinedInput-root': {
    '& fieldset': { border: 'none' }
  },
  '& .MuiInputBase-root': {
    borderBottom: '1px solid #C9CACC',
    borderRadius: 0,
    pl: 0,
  },
  '& :focus-visible': { outline: '2px solid black', outlineOffset: '-1px' },
  '& .MuiInputBase-input': {
    pl: 0,
    fontFamily: 'AC Nord Text, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0px',
    verticalAlign: 'middle',
    color: 'black',
    ml: 1
  },
  '& .MuiInputBase-input::placeholder': {
    fontFamily: 'AC Nord Text, sans-serif',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0px',
    color: '#6D6D6F',
    opacity: 1
  }
};

export const scrollableListSx: SxProps<Theme> = {
  overflowY: { xs: 'visible', md: 'auto' },
  gap: 4,
  pb: 2,
  pt: '8px',
  px: '8px',
  display: 'flex',
  flexDirection: 'column',
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 3 },
  '&:hover': { scrollbarColor: '#C9CACC transparent' },
  '&:hover::-webkit-scrollbar-thumb': { background: '#C9CACC' }
};

export const regionBoxSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column'
};

export const regionNameSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 700,
  fontStyle: 'bold',
  fontSize: { xs: '16px', sm: '18px' },
  lineHeight: '24px',
  letterSpacing: '0px',
  textTransform: 'uppercase',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  borderRadius: '4px',
  display: 'inline-block'
};

export const linkButtonSx: SxProps<Theme> = {
  minWidth: 0,
  p: 0,
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: { xs: '14x', sm: '16px' },
  lineHeight: '22px',
  letterSpacing: '0px',
  textDecoration: 'underline',
  textDecorationStyle: 'solid',
  textDecorationThickness: 0,
  textDecorationOffset: 0,
  color: 'var(--Sem-text-colour-text-link, #1460AA)'
};

export const checkboxSx: SxProps<Theme> = {
  p: 0,
  width: 24,
  height: 24,
  opacity: 1,
  '& .MuiSvgIcon-root': { width: 24, height: 24 },
  color: '#1460AA',
  '&.Mui-checked': { color: '#1460AA' },
  '&.Mui-focusVisible': {
    backgroundColor: 'transparent',
    outline: '1px solid black',
    outlineOffset: '2px',
    '& .MuiTouchRipple-root': { display: 'none' }
  }
};

export const countryNameSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

// ─── Step 1 – right panel (selected chips) ────────────────────────────────────

export const rightPanelSx: SxProps<Theme> = {
  bgcolor: '#F1F7FB',
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'visible'
};

export const selectedPanelTitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 700,
  fontStyle: 'bold',
  fontSize: { xs: '16px', sm: '18px' },
  lineHeight: '24px',
  letterSpacing: '0px',
  textTransform: 'uppercase',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

export const chipContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 2,
  pb: 3,
  px: '8px',
  pt: '8px',
  overflowY: { xs: 'visible', md: 'auto' },
  flex: { xs: 'none', md: 1 },
  alignContent: 'flex-start',
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 3 },
  '&:hover': { scrollbarColor: '#C9CACC transparent' },
  '&:hover::-webkit-scrollbar-thumb': { background: '#C9CACC' }
};

export const chipSx: SxProps<Theme> = {
  height: 40,
  gap: 1,
  opacity: 1,
  paddingTop: '8px',
  paddingRight: '16px',
  paddingBottom: '8px',
  paddingLeft: '16px',
  borderRadius: '9999px',
  borderWidth: '1px',
  borderStyle: 'solid',
  maxWidth: '100%',
  backgroundColor:
    'var(--Sem-object-fills-colour-bg-fill-secondary-pressed, #EFF0F2) !important',
  borderColor: 'var(--Sem-brdr-colour-border-primary-pressed, #002345)',
  '& .MuiChip-label': { paddingLeft: 0, paddingRight: 1, overflow: 'hidden' }
};

export const chipLabelStyle: CSSProperties = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  textAlign: 'center',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-label-pressed, #002345)'
};

// ─── Footer ───────────────────────────────────────────────────────────────────

export const footerActionsSx: SxProps<Theme> = {
  justifyContent: 'space-between',
  gap: { sm: 0, xs: 3 },
  flexDirection: { sm: 'row', xs: 'column-reverse' },
  opacity: 1,
  py: { sm: 2, xs: 2 },
  px: { sm: 2, xs: 2 },
  background: 'var(--Sem-object-fills-colour-bg-fill-card-primary, #FFFFFF)',
  boxShadow: '0px 0px 15px 0px #00000026'
};

export const footerCountTextSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: { xs: '14px', sx: '18px' },
  lineHeight: '24px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
  background: 'transparent'
};

// ─── Step 2 – IATA entry ──────────────────────────────────────────────────────

export const iatasContentSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  flex: 1,
  minHeight: 0,
  p: 0,
  overflow: 'auto'
};

// ─── Image styles ─────────────────────────────────────────────────────────────

export const flagImageStyle: CSSProperties = {
  width: 24,
  height: 14,
  opacity: 1,
  display: 'block',
  objectFit: 'contain'
};

export const searchIconStyle: CSSProperties = {
  width: 24,
  height: 24,
  display: 'block',
  objectFit: 'contain'
};

export const closeIconStyle: CSSProperties = {
  width: 20,
  height: 20,
  display: 'block',
  objectFit: 'contain'
};

export const chipDeleteIconStyle: CSSProperties = {
  width: 14,
  height: 14,
  display: 'block',
  objectFit: 'contain'
};
